import IconButtonM from "@common/components/button/IconButtonM"
import TextM from "@common/components/text/TextM"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { clearLastPhoto } from "@data/redux/actions/camera"
import { displayTimeSeconds } from "@data/utility/Utils"
import React, { useContext, useEffect, useRef, useState } from "react"
import { Dimensions, FlatList, View } from "react-native"
import { TapGestureHandler } from "react-native-gesture-handler"
import { IconButton } from "react-native-paper"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { connect } from "react-redux"
import { theme } from "../../theme/theme"
import { DimensionsContext, IS_WEB } from "../../utils/mobileUtils"
import { PinchableBox } from "./PinchableBox"
import { resolveParentCommand, resolveScrollIndex } from "./imageViewerIndex"
import AnnotationCanvas from "@common/liveMode/liveV3/AnnotationCanvas"

// On the web each page snaps (CSS scroll snap). RN-web's own pagingEnabled
// replaces the cell's style with its snap style, and the page loses its row
// layout: the photo collapsed to a height of 0.
function SnapCell({ style, cellKey, index, item, ...props }) {
    return <View style={[style, { scrollSnapAlign: "start" }]} {...props} />
}

function ImageViewer(props) {
    const {
        images,
        imageIndex = 0,
        close,
        withChrono,
        time,
        width = null,
        clearMode = false,
        headerLeftComponent,
        aboveControlsComponent,
        // Optional absolute-fill overlay rendered inside each image's zoom box
        // (follows pinch/rotate with the image) — e.g. correction markers.
        renderImageOverlay,
        // Notifies the parent when the displayed image changes (swipe/arrows),
        // for surfaces that mirror the current page (e.g. marker ↔ card sync).
        onIndexChange,
        // Optional extra node appended to the zoom/rotate controls row.
        extraControlComponent,
        // Annotation props (optional — only when screen share is active)
        annotationEnabled,
        annotationColor,
        annotationStrokeWidth,
        getStrokesRef,
        onStrokeEnd,
        activeCanvasRef,
    } = props
    const flatListRef = useRef()
    // `?? 0` reste nécessaire malgré la valeur par défaut : le parent passe un
    // `null` explicite quand il ne pilote pas, et un défaut ne le rattrape pas.
    const initialIndex = imageIndex ?? 0
    const [currentIndex, setCurrentIndex] = useState(initialIndex)
    const pinchableBoxRefs = useRef({})
    const activeZoomRef = useRef(null)
    const canvasRefs = useRef({})
    // Dernier index échangé avec le parent, dans un sens ou dans l'autre : c'est
    // lui qui distingue une commande d'un écho.
    const handledIndexRef = useRef(initialIndex)
    // Index visé par un défilement qu'on a demandé, tant qu'il n'est pas arrivé.
    const pendingTargetRef = useRef(null)
    const currentIndexRef = useRef(initialIndex)
    // `initialScrollIndex` est lu au montage : lui passer un état qui change
    // relance le calcul de fenêtre de la liste à chaque page.
    const initialIndexRef = useRef(initialIndex)

    const scrollToIndex = (index) => {
        pendingTargetRef.current = index
        flatListRef?.current?.scrollToIndex({ index })
        setCurrentIndex(index)
    }

    // Dès que l'utilisateur prend la liste en main, le défilement qu'on avait
    // demandé n'a plus cours : sans cette annulation, une commande interrompue
    // en route n'arrive jamais et le visionneur cesse de rapporter ses pages.
    // Câblée sur trois entrées faute d'une qui couvre les deux plateformes :
    // react-native-web n'appelle jamais onScrollBeginDrag, et onWheel est un
    // évènement DOM, inerte sur natif.
    const cancelPendingScroll = () => {
        pendingTargetRef.current = null
    }

    const goToPage = (index) => {
        activeZoomRef.current?.zoomOut()
        scrollToIndex(index)
    }

    const dimensions = useContext(DimensionsContext)
    const insets = useSafeAreaInsets()

    useEffect(() => {
        const { apply, index } = resolveParentCommand({
            imageIndex,
            handledIndex: handledIndexRef.current,
            currentIndex: currentIndexRef.current,
        })
        if (index !== null) handledIndexRef.current = index
        if (apply) scrollToIndex(index)
    }, [imageIndex])

    // Update active zoom ref and active canvas ref when current index changes
    useEffect(() => {
        currentIndexRef.current = currentIndex
        activeZoomRef.current = pinchableBoxRefs.current[currentIndex] || null
        if (activeCanvasRef) {
            activeCanvasRef.current = canvasRefs.current[currentIndex] || null
        }
        // Ne rapporter que ce que le parent ne sait pas déjà : lui renvoyer sa
        // propre commande relançait le cycle commande → rendu → commande.
        if (currentIndex !== handledIndexRef.current) {
            handledIndexRef.current = currentIndex
            onIndexChange?.(currentIndex)
        }
    }, [currentIndex])

    // Handle scroll events to update currentIndex
    const handleScroll = (event) => {
        const { index, arrived } = resolveScrollIndex({
            offset: event.nativeEvent.contentOffset.x,
            itemWidth,
            currentIndex: currentIndexRef.current,
            pendingTarget: pendingTargetRef.current,
        })
        if (arrived) pendingTargetRef.current = null
        if (index !== null) setCurrentIndex(index)
    }

    let mWidth = dimensions?.width / 2 - 60
    let adaptedWidth = mWidth < 140 ? mWidth : 140
    const itemWidth = width ?? Dimensions?.get("window")?.width
    const buttonsColor = clearMode ? theme.colors.primary : theme.colors.white

    return (
        // minHeight:0 (here and on the list): RN-web flex children default to
        // min-height:auto, so the window-tall image items would otherwise pin
        // the list's height and push the zoom/arrows controls out of view
        // whenever the viewer sits in a pane shorter than the window.
        <View style={{ flex: 1, minHeight: 0 }}>
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    paddingBottom: 5,
                    width: itemWidth,
                }}
            >
                {withChrono && (
                    <TapGestureHandler numberOfTaps={30}>
                        <View
                            style={{
                                width: 2 * adaptedWidth,
                                borderColor: theme.colors.primary,
                                borderRadius: adaptedWidth,
                                alignItems: "center",
                                justifyContent: "center",
                                alignSelf: "center",
                                position: "absolute",
                                top: 5 + insets.top,
                                left: "50%",
                                transform: [{ translateX: -adaptedWidth }],
                            }}
                        >
                            <TextM
                                fontWeight="Medium"
                                fontSize={40}
                                color={buttonsColor}
                            >
                                {displayTimeSeconds(time)}
                            </TextM>
                        </View>
                    </TapGestureHandler>
                )}

                {/* Header left component (Like/Dislike buttons) */}
                {headerLeftComponent && headerLeftComponent()}

                {/* Separator */}
                {headerLeftComponent && (
                    <View
                        style={{
                            width: 1,
                            height: 24,
                            backgroundColor: buttonsColor,
                            opacity: 0.3,
                            marginHorizontal: 8,
                        }}
                    />
                )}

                {/* Close button */}
                {!!close && (
                    <IconButton
                        icon={"times"}
                        iconColor={buttonsColor}
                        containerColor={theme.colors.transparent}
                        size={32}
                        onPress={close}
                    />
                )}
            </View>

            <FlatList
                ref={flatListRef}
                data={images}
                pinchGestureEnabled={!annotationEnabled}
                style={{
                    flex: 1,
                    minHeight: 0,
                    ...(IS_WEB && { scrollSnapType: "x mandatory" }),
                }}
                // One photo per swipe, on the web too: the list stops on a
                // page, never between two.
                CellRendererComponent={IS_WEB ? SnapCell : undefined}
                pagingEnabled={!IS_WEB && !annotationEnabled}
                scrollEnabled={!annotationEnabled}
                showsHorizontalScrollIndicator={false}
                initialScrollIndex={initialIndexRef.current}
                horizontal
                onScroll={handleScroll}
                onMomentumScrollEnd={handleScroll}
                onScrollBeginDrag={cancelPendingScroll}
                onTouchMove={cancelPendingScroll}
                onWheel={cancelPendingScroll}
                scrollEventThrottle={16}
                getItemLayout={(data, index) => ({
                    length: itemWidth,
                    offset: itemWidth * index,
                    index,
                })}
                renderItem={({ item, index }) => {
                    return (
                        <PinchableBox
                            imageUri={item}
                            imageStyle={{
                                width: itemWidth,
                                height: dimensions?.height,
                            }}
                            gesturesEnabled={!annotationEnabled}
                            ref={(ref) => {
                                if (ref) {
                                    pinchableBoxRefs.current[index] = ref
                                    if (index === currentIndex) {
                                        activeZoomRef.current = ref
                                    }
                                }
                            }}
                        >
                            {getStrokesRef && (
                                <AnnotationCanvas
                                    ref={(r) => {
                                        if (r) {
                                            canvasRefs.current[index] = r
                                            if (
                                                index === currentIndex &&
                                                activeCanvasRef
                                            ) {
                                                activeCanvasRef.current = r
                                            }
                                        }
                                    }}
                                    width={itemWidth}
                                    height={dimensions?.height}
                                    color={annotationColor}
                                    strokeWidth={annotationStrokeWidth}
                                    enabled={annotationEnabled}
                                    strokesRef={getStrokesRef(index)}
                                    onStrokeEnd={() => onStrokeEnd?.(index)}
                                />
                            )}
                            {renderImageOverlay?.(index)}
                        </PinchableBox>
                    )
                }}
            />

            {aboveControlsComponent && (
                <View style={{ paddingHorizontal: 16, paddingTop: 16 }}>
                    {aboveControlsComponent()}
                </View>
            )}

            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {images?.length > 1 && (
                    <IconButtonM
                        size={40}
                        icon={"angle-left"}
                        disabled={currentIndex === 0}
                        iconColor={buttonsColor}
                        onPress={() =>
                            currentIndex > 0 && goToPage(currentIndex - 1)
                        }
                    />
                )}

                {IS_WEB && (
                    <IconButtonM
                        size={30}
                        icon={"minus-circle"}
                        iconColor={buttonsColor}
                        onPress={() => {
                            if (activeZoomRef.current) {
                                activeZoomRef.current.zoomOut()
                            }
                        }}
                    />
                )}

                <IconButtonM
                    size={30}
                    icon={"redo"}
                    iconColor={buttonsColor}
                    onPress={() => {
                        if (activeZoomRef.current) {
                            activeZoomRef.current.rotate()
                        }
                    }}
                />

                {IS_WEB && (
                    <IconButtonM
                        size={30}
                        icon={"plus-circle"}
                        iconColor={buttonsColor}
                        onPress={() => {
                            if (activeZoomRef.current) {
                                activeZoomRef.current.zoomIn()
                            }
                        }}
                    />
                )}

                {extraControlComponent ?? null}

                {images?.length > 1 && (
                    <IconButtonM
                        size={40}
                        icon={"angle-right"}
                        disabled={currentIndex === images?.length - 1}
                        iconColor={buttonsColor}
                        onPress={() =>
                            currentIndex < images?.length - 1 &&
                            goToPage(currentIndex + 1)
                        }
                    />
                )}
            </View>
        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        lastPhoto: state.camera?.lastPhoto,
        lastPhotoFormId: state.camera?.formId,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    clearLastPhoto,
}

export default connect(mapStateToProps, mapDispatchToProps)(ImageViewer)
